// Single source of truth for IP-camera bandwidth math. Consumed by:
//   - src/pages/calculator/BandwidthCalculator.tsx (standalone calculator)
//   - src/components/cctv-designer-v2/NetworkAnalysisPanel.tsx (per-project)
//   - src/components/cctv-designer-v2/VlanQosAdvisorPanel.tsx (switch tier)
//
// Math sourced from manufacturer datasheets (Hikvision, Axis, Bosch) and
// EN 62676-4. Verified against the standalone calculator results before
// extraction so the two views never drift.

export type Codec = 'h264' | 'h265' | 'h265plus' | 'av1';
export type SceneComplexity = 'static' | 'medium' | 'complex';
export type StreamMode = 'main' | 'sub' | 'both';
export type RecordingTrigger = 'continuous' | 'motion' | 'tripwire';
export type FpsMode = 'global' | 'auto' | 'manual';

// Substream is typically 1/4 frame rate at 1/2 vertical resolution = ~1/8 raw
// pixel data. After codec encoding the ratio compresses to ~15-22% of main
// stream bitrate. 0.2 (20%) matches Hikvision / Dahua / Axis factory-default
// secondary stream.
export const SUBSTREAM_RATIO = 0.2;

// Reference H.264 CBR bitrates at 30 fps, medium scene, 2 s I-frame interval.
// Sourced from manufacturer datasheets (Hikvision, Axis, Bosch typical values).
export const BITRATE_MAP_H264: Record<number, number> = {
  1: 2,
  2: 4,
  4: 8,
  5: 10,
  8: 16,
  12: 24,
  16: 32,
};

// Codec efficiency vs H.264 baseline.
//   H.265 (HEVC):     ~50% smaller at same perceptual quality
//   H.265+ (Smart):   ~70% smaller (Hikvision/Dahua dynamic ROI compression)
//   AV1:              ~65% smaller vs H.264 (~30% better than H.265)
export const CODEC_MULTIPLIER: Record<Codec, number> = {
  h264: 1.0,
  h265: 0.5,
  h265plus: 0.3,
  av1: 0.35,
};

// Scene complexity affects rate-controlled VBR streams. Most CCTV is medium;
// static scenes at night drop dramatically, busy traffic/sports rise.
export const SCENE_MULTIPLIER: Record<SceneComplexity, number> = {
  static: 0.6,
  medium: 1.0,
  complex: 1.6,
};

// Typical frame rates by camera type when the user picks "auto from camera
// type" instead of forcing a single global fps. Most fixed cameras run 25 fps;
// panoramic/fisheye chipsets sustain a lower native rate; PTZ defaults stay
// at 25 since the AI tracking pipeline needs it.
export const FPS_BY_CAMERA_TYPE: Record<string, number> = {
  bullet: 25,
  dome: 25,
  turret: 25,
  ptz: 25,
  panoramic: 15,
  fisheye: 15,
  thermal: 8,
};

export interface BandwidthSettings {
  codec: Codec;
  scene: SceneComplexity;
  fps: number;
  iframeInterval: number;
  audioEnabled: boolean;
  streamMode: StreamMode;
  recordingTrigger: RecordingTrigger;
  /** 1-100 — only used when recordingTrigger !== 'continuous'. */
  activityPercent: number;
  liveViewers: number;
  /** Override substream ratio (default 0.2). */
  substreamRatio?: number;
}

export interface CameraBandwidthInput {
  id: string;
  name?: string;
  /** Camera type for "fps auto" mode; ignored otherwise. */
  type?: string;
  megapixels: number;
  /** Per-camera FPS mode. undefined or 'global' → use settings.fps. */
  fpsMode?: FpsMode;
  /** Only used when fpsMode === 'manual'. */
  fps?: number;
  codecOverride?: Codec;
  sceneOverride?: SceneComplexity;
  /** Hard override — skips the whole bitrate calculation when set. Mbps. */
  bitrateOverride?: number;
}

export interface PerCameraBandwidth {
  id: string;
  name?: string;
  mainMbps: number;
  subMbps: number;
  /** Per-camera contribution to NVR ingest, after duty-cycle adjustment. */
  recordingMbps: number;
  /** Per-camera contribution to live-view egress (× viewers). */
  liveMonMbps: number;
  effectiveFps: number;
  source: 'override' | 'computed';
}

export interface SwitchUplinkRecommendation {
  label: string;
  tone: 'green' | 'yellow' | 'red';
  recommendedGbps: 1 | 10;
}

export interface ProjectBandwidth {
  perCamera: PerCameraBandwidth[];
  /** Average recording ingest (duty-cycle adjusted). */
  recordingTotalMbps: number;
  /** Peak recording ingest — every camera triggers simultaneously. */
  recordingPeakMbps: number;
  liveMonTotalMbps: number;
  /** max(recordingTotalMbps, liveMonTotalMbps). */
  networkTotalMbps: number;
  switchUplink: SwitchUplinkRecommendation;
  needsGigabit: boolean;
  needs10G: boolean;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// Interpolate base bitrate for non-standard sensor sizes (3MP, 6MP, 10MP).
// The catalog progression in BITRATE_MAP_H264 is linear ≈ 2 Mbps per MP at
// the H.264 30fps medium baseline, so the fallback follows the same slope.
function baseBitrateH264(megapixels: number): number {
  if (BITRATE_MAP_H264[megapixels] !== undefined) return BITRATE_MAP_H264[megapixels];
  return Math.max(1, megapixels * 2);
}

export function resolveEffectiveFps(
  camera: CameraBandwidthInput,
  settings: BandwidthSettings,
): number {
  if (camera.fpsMode === 'manual' && typeof camera.fps === 'number') {
    return camera.fps;
  }
  if (camera.fpsMode === 'auto' && camera.type) {
    return FPS_BY_CAMERA_TYPE[camera.type] ?? settings.fps;
  }
  return settings.fps;
}

export function computeCameraBitrate(
  camera: CameraBandwidthInput,
  settings: BandwidthSettings,
): number {
  if (typeof camera.bitrateOverride === 'number') {
    return round2(camera.bitrateOverride);
  }

  const base = baseBitrateH264(camera.megapixels);
  const fpsScale = resolveEffectiveFps(camera, settings) / 30;
  const iframeScale = 2 / settings.iframeInterval; // shorter GOP = higher bitrate
  const codec = camera.codecOverride ?? settings.codec;
  const scene = camera.sceneOverride ?? settings.scene;
  const codecScale = CODEC_MULTIPLIER[codec];
  const sceneScale = SCENE_MULTIPLIER[scene];
  const audioOverhead = settings.audioEnabled ? 0.128 : 0; // 128 kbps audio

  return round2(base * fpsScale * iframeScale * codecScale * sceneScale + audioOverhead);
}

export function recommendSwitchUplink(networkTotalMbps: number): SwitchUplinkRecommendation {
  if (networkTotalMbps <= 80) return { label: '1 Gbps', tone: 'green', recommendedGbps: 1 };
  if (networkTotalMbps <= 800) {
    return { label: '1 Gbps (saturating ≥ 80%)', tone: 'yellow', recommendedGbps: 1 };
  }
  return { label: '10 Gbps required', tone: 'red', recommendedGbps: 10 };
}

export function computeProjectBandwidth(
  cameras: CameraBandwidthInput[],
  settings: BandwidthSettings,
): ProjectBandwidth {
  const ratio = settings.substreamRatio ?? SUBSTREAM_RATIO;
  const dutyCycle =
    settings.recordingTrigger === 'continuous' ? 1 : Math.max(0, settings.activityPercent) / 100;

  // Per-camera fields get round2 (UI displays them). Aggregate totals sum the
  // un-rounded products so the project view matches the standalone calculator
  // bit-for-bit at any camera count.
  let recTotalRaw = 0;
  let recPeakRaw = 0;
  let liveTotalRaw = 0;

  const perCamera: PerCameraBandwidth[] = cameras.map((cam) => {
    const mainMbps = computeCameraBitrate(cam, settings);
    const subRaw = mainMbps * ratio;
    const subMbps = round2(subRaw);

    // Recording uses the substream only when streamMode === 'sub' (rare —
    // substream-only recording). 'both' = main records, sub serves live.
    const recPerCamRaw = settings.streamMode === 'sub' ? subRaw : mainMbps;
    const recRaw = recPerCamRaw * dutyCycle;

    // Live monitoring falls back to main only when streamMode === 'main'
    // (legacy/no substream). Otherwise viewers pull the sub.
    const livePerCamRaw = settings.streamMode === 'main' ? mainMbps : subRaw;
    const liveRaw = livePerCamRaw * settings.liveViewers;

    recTotalRaw += recRaw;
    recPeakRaw += recPerCamRaw;
    liveTotalRaw += liveRaw;

    return {
      id: cam.id,
      name: cam.name,
      mainMbps,
      subMbps,
      recordingMbps: round2(recRaw),
      liveMonMbps: round2(liveRaw),
      effectiveFps: resolveEffectiveFps(cam, settings),
      source: typeof cam.bitrateOverride === 'number' ? 'override' : 'computed',
    };
  });

  const recordingTotalMbps = round2(recTotalRaw);
  const recordingPeakMbps = round2(recPeakRaw);
  const liveMonTotalMbps = round2(liveTotalRaw);
  const networkTotalMbps = round2(Math.max(recordingTotalMbps, liveMonTotalMbps));

  return {
    perCamera,
    recordingTotalMbps,
    recordingPeakMbps,
    liveMonTotalMbps,
    networkTotalMbps,
    switchUplink: recommendSwitchUplink(networkTotalMbps),
    needsGigabit: networkTotalMbps > 80,
    needs10G: networkTotalMbps > 800,
  };
}

/** Default settings — match the BandwidthCalculator's initial state. */
export function defaultBandwidthSettings(): BandwidthSettings {
  return {
    codec: 'h265',
    scene: 'medium',
    fps: 25,
    iframeInterval: 2,
    audioEnabled: false,
    streamMode: 'main',
    recordingTrigger: 'continuous',
    activityPercent: 25,
    liveViewers: 0,
  };
}

// ─── NVR ingest validation ──────────────────────────────────────────────
// Caller-agnostic descriptor: any NVR-like recorder with a published
// input-bandwidth spec fits. Designer V2 builds these from EQUIPMENT_CATALOG;
// callers can build them from anywhere else (e.g. tests, SmartQuote).

export interface NvrIngestDescriptor {
  id: string;
  label?: string;
  /** Datasheet "input bandwidth" / "incoming bitrate" cap, in Mbps. */
  inputBandwidthMbps: number;
  /** Optional — only used for display. */
  channels?: number;
}

export interface NvrIngestAssessment {
  totalCapacityMbps: number;
  totalRecordingMbps: number;
  /** recordingTotalMbps / totalCapacityMbps × 100. Can exceed 100. */
  utilizationPercent: number;
  tone: 'green' | 'yellow' | 'red';
  status: 'ok' | 'warning' | 'overflow';
}

/**
 * Compare aggregate recording bandwidth to combined NVR input capacity.
 * Returns null when there are no NVRs in the project — there's nothing to
 * validate against and the panel should hide the section.
 *
 * Thresholds match the user's locked decision (Faza 3):
 *   - < 80% utilization → green / 'ok'
 *   - 80–100%           → yellow / 'warning'
 *   - > 100%            → red / 'overflow'
 */
export function validateNvrIngest(
  recordingTotalMbps: number,
  nvrs: NvrIngestDescriptor[],
): NvrIngestAssessment | null {
  if (nvrs.length === 0) return null;
  const totalCapacityMbps = nvrs.reduce((s, n) => s + n.inputBandwidthMbps, 0);
  if (totalCapacityMbps <= 0) return null;
  const utilizationPercent = Math.round((recordingTotalMbps / totalCapacityMbps) * 1000) / 10;
  let tone: NvrIngestAssessment['tone'] = 'green';
  let status: NvrIngestAssessment['status'] = 'ok';
  if (utilizationPercent > 100) {
    tone = 'red';
    status = 'overflow';
  } else if (utilizationPercent >= 80) {
    tone = 'yellow';
    status = 'warning';
  }
  return {
    totalCapacityMbps,
    totalRecordingMbps: recordingTotalMbps,
    utilizationPercent,
    tone,
    status,
  };
}
